import { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import CookieBanner from "@/components/CookieBanner";
import { CookiePrefsProvider } from "@/components/CookiePrefsContext";
import ManagePreferencesModal from "@/components/ManagePreferencesModal";

import Home from "@/pages/Home";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Terms from "@/pages/Terms";
import ThankYou from "@/pages/ThankYou";

const SUPPORTED = ["hr", "en"];

function RootRedirect() {
  const browserLang = (typeof navigator !== "undefined" && navigator.language ? navigator.language : "hr").slice(0, 2).toLowerCase();
  const target = SUPPORTED.includes(browserLang) ? browserLang : "hr";
  return <Navigate to={`/${target}`} replace />;
}

function LangGuard({ children }) {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (!SUPPORTED.includes(lang)) return;
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang, i18n, location.pathname]);

  if (!SUPPORTED.includes(lang)) {
    return <Navigate to="/hr" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CookiePrefsProvider>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<RootRedirect />} />

            <Route
              path="/thank_you"
              element={<ThankYou />}
            />

            <Route
              path="/:lang"
              element={
                <LangGuard>
                  <Layout>
                    <Home />
                  </Layout>
                </LangGuard>
              }
            />
            <Route
              path="/:lang/privacy"
              element={
                <LangGuard>
                  <Layout>
                    <PrivacyPolicy />
                  </Layout>
                </LangGuard>
              }
            />
            <Route
              path="/:lang/cookies"
              element={
                <LangGuard>
                  <Layout>
                    <CookiePolicy />
                  </Layout>
                </LangGuard>
              }
            />
            <Route
              path="/:lang/terms"
              element={
                <LangGuard>
                  <Layout>
                    <Terms />
                  </Layout>
                </LangGuard>
              }
            />

            <Route path="*" element={<Navigate to="/hr" replace />} />
          </Routes>

          <CookieBanner />
          <ManagePreferencesModal />
          <Toaster />
        </CookiePrefsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
